/**
 * System Settings API — key-value store at /api/auth/settings/.
 */

import client from './client';

/** Fetch all settings as array. */
export async function fetchSettings() {
  const { data } = await client.get('/auth/settings/');
  return data.data || data.results || data;
}

/** Update a single setting by ID. */
export async function updateSetting(id, payload) {
  const { data } = await client.patch(`/auth/settings/${id}/`, payload);
  return data.data || data;
}

/** Create a new setting. */
export async function createSetting(payload) {
  const { data } = await client.post('/auth/settings/', payload);
  return data.data || data;
}

/**
 * Upsert helper — finds by key then updates, or creates if missing.
 * Returns the saved setting object.
 */
export async function upsertSetting(key, value) {
  const all = await fetchSettings();
  const existing = (Array.isArray(all) ? all : []).find((s) => s.key === key);
  if (existing) {
    return updateSetting(existing.id, { value: String(value) });
  }
  return createSetting({ key, value: String(value) });
}

/**
 * Bulk upsert — save multiple key-value pairs.
 * @param {Record<string, string>} kvPairs — { key: value, ... }
 */
export async function bulkUpsertSettings(kvPairs) {
  const all = await fetchSettings();
  const map = {};
  (Array.isArray(all) ? all : []).forEach((s) => { map[s.key] = s; });

  const promises = Object.entries(kvPairs).map(([key, value]) => {
    const existing = map[key];
    if (existing) {
      return updateSetting(existing.id, { value: String(value) });
    }
    return createSetting({ key, value: String(value) });
  });

  return Promise.all(promises);
}

/** Fetch email diagnostics (unmatched count, last poll, etc). */
export async function fetchEmailDiagnostics() {
  try {
    const [unmatched] = await Promise.all([
      client.get('/email/unmatched/', { params: { limit: 1 } }),
    ]);
    return {
      unmatched_count: unmatched.data?.count ?? unmatched.data?.data?.length ?? 0,
    };
  } catch {
    return { unmatched_count: 0 };
  }
}

/* ── Training Notes API ── */

/** Fetch all training notes. */
export async function fetchTrainingNotes() {
  const { data } = await client.get('/email/training-notes/');
  return data.data || data.results || data;
}

/** Create a new training note. */
export async function createTrainingNote(payload) {
  const { data } = await client.post('/email/training-notes/', payload);
  return data.data || data;
}

/** Update a training note. */
export async function updateTrainingNote(id, payload) {
  const { data } = await client.patch(`/email/training-notes/${id}/`, payload);
  return data.data || data;
}

/** Delete a training note. */
export async function deleteTrainingNote(id) {
  await client.delete(`/email/training-notes/${id}/`);
}
